import React from 'react';
import { formatByHint } from './criteriaBarUtils';
import { getIntentTone } from './intentTone';

const unicodeMinus = '\u2212';

export default function ValueChangeBlock({
  valueChangeDisplay,
  intent,
  embedded = false,
  className = '',
  afterEditable = false,
  afterRawValue = '',
  onAfterRawChange,
  afterLabel = 'Nuevo presupuesto',
}) {
  if (!valueChangeDisplay) return null;
  const tone = getIntentTone(intent);
  const currency = valueChangeDisplay.currency || 'USD';
  const hint = valueChangeDisplay.format_hint || 'decimal_2';

  const before = formatByHint(valueChangeDisplay.value_before, hint, currency);
  const after = formatByHint(valueChangeDisplay.value_after, hint, currency);

  const direction = String(valueChangeDisplay.direction || '').toLowerCase();
  const isDecrease = direction === 'decrease';
  const sign = isDecrease ? unicodeMinus : '+';

  const deltaAbsFormatted =
    valueChangeDisplay.delta_absolute == null
      ? null
      : formatByHint(Math.abs(valueChangeDisplay.delta_absolute), hint, currency);

  const deltaPctFormatted = (() => {
    const p = valueChangeDisplay.delta_percent;
    if (p == null) return null;
    if (!Number.isFinite(Number(p))) return '—';
    return `${sign}${Number(Math.abs(p)).toFixed(1)}%`;
  })();

  const pctOnlyText = (() => {
    if (valueChangeDisplay.value_after === valueChangeDisplay.value_before) return 'Sin cambio';
    return deltaPctFormatted || '—';
  })();

  const absOnlyText = (() => {
    if (valueChangeDisplay.value_after === valueChangeDisplay.value_before) return null;
    return deltaAbsFormatted ? `${sign}${deltaAbsFormatted}` : null;
  })();

  return (
    <section
      className={[
        embedded ? '' : 'mt-4 rounded-2xl border border-white/10',
        embedded ? '' : 'bg-white/5',
        className,
      ].filter(Boolean).join(' ')}
      style={embedded ? undefined : { background: tone.bg }}
    >
      <div className="px-4 py-3 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-white/70">{valueChangeDisplay.field_label || 'Valor actual'}</div>
          <div className="mt-1 text-[22px] font-semibold text-white tabular-nums truncate">{before}</div>
        </div>

        <div className="min-w-[100px] flex flex-col items-center">
          <div className="text-white/70 text-[22px]" aria-hidden>→</div>
          <span
            className="mt-1 inline-flex items-center rounded-full px-2 py-[2px] text-[11px] font-semibold whitespace-nowrap border border-white/10"
            style={{ background: tone.pillBg, color: tone.surface }}
          >
            {pctOnlyText}
          </span>
          {absOnlyText ? (
            <div className="mt-1 text-[11px] font-semibold tabular-nums text-white/70 whitespace-nowrap">
              {absOnlyText}
            </div>
          ) : null}
        </div>

        <div className="flex-1 min-w-0 text-right">
          <div className="text-[11px] text-white/70">{afterLabel}</div>
          {afterEditable ? (
            <div className="mt-1 flex items-center justify-end gap-2">
              <span className="text-xs font-semibold text-white/60 tabular-nums">
                {currency}
              </span>
              <input
                inputMode="numeric"
                type="number"
                min="0"
                step="1"
                value={afterRawValue ?? ''}
                onChange={(e) => onAfterRawChange?.(e.target.value)}
                className="w-[140px] h-10 rounded-lg bg-white/10 border border-white/20 px-3 text-[18px] font-semibold tabular-nums text-right text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                aria-label="Nuevo presupuesto"
              />
            </div>
          ) : (
            <div className="mt-1 text-[22px] font-semibold tabular-nums truncate" style={{ color: tone.surface }}>
              {after}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

